import React from 'react';
import AnswerOption from './AnswerOption';

/**
 * Question Component
 * Displays a single question with its answer options
 * Auto-advances to next question after answer selection
 * 
 * @param {object} question - Question object from questions.js
 * @param {object} section - Section object that contains this question
 * @param {string} selectedAnswer - ID of currently selected answer
 * @param {function} onAnswer - Callback when answer is selected (includes auto-advance)
 * @param {function} onBack - Callback for back button
 * @param {boolean} canGoBack - Whether back button should be enabled
 * @param {boolean} isLastQuestion - Whether this is the last question
 */
export default function Question({ 
  question, 
  section, 
  selectedAnswer, 
  onAnswer,
  onBack,
  canGoBack,
  isLastQuestion
}) {
  return (
    <div className="min-h-screen flex items-center justify-center p-3 sm:p-4">
      <div className="max-w-xl md:max-w-2xl w-full">
        {/* Section Label */}
        <div className="text-center mb-2 sm:mb-3">
          <span className="text-teal-accent text-xs sm:text-sm font-semibold tracking-wider uppercase">
            {section.name}
          </span>
        </div>
        
        {/* Question Text */}
        <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-center mb-4 sm:mb-6 px-2 sm:px-4">
          {question.text}
        </h2>
        
        {/* Answer Options */}
        <div className="space-y-2.5 sm:space-y-3 mb-4 sm:mb-6">
          {question.answers.map((answer) => (
            <AnswerOption
              key={answer.id}
              answer={answer}
              selected={selectedAnswer === answer.id}
              onSelect={onAnswer}
            />
          ))}
        </div>
        
        {/* Back Button (centered) */}
        {canGoBack && (
          <div className="flex justify-center">
            <button
              onClick={onBack}
              className="btn-secondary"
              aria-label="Previous question"
            >
              ← Back
            </button>
          </div>
        )}
        
        {/* Helper text */}
        {!selectedAnswer && (
          <p className="text-center text-gray-400 text-xs sm:text-sm mt-3 sm:mt-4">
            Select an answer to continue
          </p>
        )}
      </div>
    </div>
  );
}

